// src/components/roles/RoleForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  alpha,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { styled } from '@mui/material/styles';

// Import components
import PermissionMatrix from '../permissions/PermissionMatrix';
import MagicTextField from '../ui/FormFields/MagicTextField';
import { Button } from '../../design-system/components/core';

// Import stores and services
import useRoleStore from '../../stores/roleStore';

// Styled components
const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  // borderRadius: theme.shape.borderRadius * 2,
  backgroundImage: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.7)} 0%, ${theme.palette.background.paper} 100%)`,
  boxShadow: theme.shadows[1],
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
      : `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
  },
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
  '&::before, &::after': {
    borderColor: alpha(theme.palette.divider, 0.2),
  },
}));

/**
 * Form for creating/editing roles
 * 
 * @param {Object} props
 * @param {Object} [props.role] - Role to edit (omit for create mode)
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Function} props.onCancel - Callback when form is cancelled
 */
const RoleForm = ({ role, onSubmit, onCancel }) => {
  const [error, setError] = useState(null);
  const { loading, constants, fetchPermissionConstants } = useRoleStore();
  const { resources, actions, scopes } = constants;
  
  // Check if we're in edit mode
  const isEditMode = Boolean(role);
  
  // Load permission constants when component mounts
  useEffect(() => {
    fetchPermissionConstants();
  }, [fetchPermissionConstants]);
  
  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(50, 'Name must be at most 50 characters'),
    key: Yup.string()
      .required('Key is required')
      .matches(/^[a-z0-9_]+$/, 'Key must contain only lowercase letters, numbers, and underscores')
      .min(3, 'Key must be at least 3 characters')
      .max(30, 'Key must be at most 30 characters'),
    description: Yup.string()
      .required('Description is required')
      .min(10, 'Description must be at least 10 characters')
      .max(200, 'Description must be at most 200 characters'),
    permissions: Yup.array().of(
      Yup.object().shape({
        resource: Yup.string().required(),
        action: Yup.string().required(),
        scope: Yup.string().required()
      })
    )
  });
  
  // Initialize formik
  const formik = useFormik({
    initialValues: {
      name: role?.name || '',
      key: role?.key || '',
      description: role?.description || '',
      permissions: role?.permissions || [],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setError(null);
        // Make sure all permissions have a scope, default to 'all' if missing
        const updatedPermissions = values.permissions.map(permission => ({
          ...permission,
          scope: permission.scope || 'all'
        }));
        await onSubmit({
          ...values,
          permissions: updatedPermissions
        });
      } catch (err) {
        setError(err.message || 'Failed to save role');
      }
    },
  });
  
  // Handle permission changes
  const handlePermissionsChange = (newPermissions) => {
    formik.setFieldValue('permissions', newPermissions);
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <FormPaper>
        {/* Form heading */}
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1.5 }}  />
          {isEditMode ? 'Edit Role' : 'Create New Role'}
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          {isEditMode 
            ? 'Modify the role details and permissions below.' 
            : 'Define a new role with specific permissions.'}
        </Typography>
        
        {/* Error display */}
        <Collapse in={Boolean(error || formik.status?.error)}>
          <Alert 
            severity="error" 
            sx={{ mt: 2, mb: 2 }}
            onClose={() => {
              setError(null);
              formik.setStatus({ error: null });
            }}
          >
            {error || formik.status?.error}
          </Alert>
        </Collapse>
        
        {/* Basic role information */}
        <Box sx={{ mb: 3, mt: 3 }}>
          <MagicTextField
            fullWidth
            magical
            id="name"
            name="name"
            label="Role Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            disabled={loading}
            placeholder="e.g., Content Manager"
            margin="normal"
          />
          
          <MagicTextField
            fullWidth
            magical
            id="key"
            name="key"
            label="Role Key"
            value={formik.values.key}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.key && Boolean(formik.errors.key)}
            helperText={
              (formik.touched.key && formik.errors.key) || 
              'Unique identifier used in the system (lowercase, no spaces)'
            }
            disabled={loading || isEditMode} // Can't change key in edit mode
            placeholder="e.g., content_manager"
            margin="normal"
          />
          
          <MagicTextField
            fullWidth
            magical
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            disabled={loading}
            placeholder="Describe the purpose and responsibilities of this role"
            multiline
            rows={3}
            margin="normal"
          />
        </Box>
        
        <SectionDivider>
          <Typography variant="body2" color="textSecondary">
            Permissions
          </Typography>
        </SectionDivider>
        
        {/* Permission matrix */}
        <PermissionMatrix
          resources={resources}
          actions={actions}
          scopes={scopes}
          selectedPermissions={formik.values.permissions}
          onChange={handlePermissionsChange}
          loading={loading}
          disabled={loading}
          descriptions={{
            resources: Object.entries(resources).reduce((acc, [key, value]) => {
              acc[value] = `Resource: ${key}`;
              return acc;
            }, {}),
            actions: Object.entries(actions).reduce((acc, [key, value]) => {
              acc[value] = `Action: ${key}`;
              return acc;
            }, {})
          }}
        />
        
        {/* Form actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3
        }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            
            startIcon={<SaveIcon />}
            loading={loading}
            magical
          >
            {isEditMode ? 'Update Role' : 'Create Role'}
          </Button>
        </Box>
      </FormPaper>
    </Box>
  );
};

export default RoleForm;